import { styled } from '@mui/material/styles';
import { Button, Card, Checkbox, Chip, TextField } from '@mui/material';
import type { RowState } from '@internal/backstage-plugin-bidone-backend';

// Shared styled() components for BDirectSectionCard — every sub-component
// (header, state dropdown, build / region cells, table chrome) imports
// from here so the visual rhythm stays coherent.

// Card with optional outer-ring "editing" highlight. Drawn as a 2px
// box-shadow rather than an inset border because the section header and
// the "Add manual step" footer have their own background colours which
// would paint over an inset border and clip the ring to the table portion
// of the card. Stacked with the default elevation so the card still reads
// as raised.
export const SectionCard = styled(Card, {
  shouldForwardProp: prop => prop !== 'editing',
})<{ editing?: boolean }>(({ theme, editing }) => ({
  marginBottom: theme.spacing(2),
  // Allow the table to scroll horizontally when fixed column widths exceed
  // the card — better than crushing cells into unreadable wraps.
  overflowX: 'auto',
  overflowY: 'hidden',
  ...(editing && {
    boxShadow: `0 0 0 2px rgb(252, 167, 0), ${theme.shadows[1]}`,
  }),
}));

export const SectionHeader = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(1.5, 2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}));

// Renders as a div (the original use site passed `component="div"`) with
// h6 typography baked in — `styled(Typography)` would strip the
// `component` prop, so we apply the theme's h6 variant directly.
export const SectionTitle = styled('div')(({ theme }) => ({
  ...theme.typography.h6,
  fontWeight: 600,
}));

// Wrapper so the title and the "updated by …" line stack vertically while
// still occupying the same flex slot in the header.
export const SectionTitleColumn = styled('div')({
  flex: 1,
  minWidth: 0,
});

export const SectionTitleField = styled('div')({
  flex: 1,
  minWidth: 0,
});

export const HeaderActions = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexShrink: 0,
}));

export const Empty = styled('div')(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  opacity: 0.6,
}));

// Real <table> rather than CSS-grid so the column widths line up across
// sections. `table-layout: fixed` + the <colgroup> in the card component
// is what actually pins those widths across sections.
//
// `minWidth` keeps fixed columns readable: when the viewport is still tight
// (even after reclaiming the side navs), the card scrolls horizontally
// instead of wrapping every cell into a vertical stack of letters.
export const Table = styled('table')(({ theme }) => ({
  width: '100%',
  // Floor includes Step @ 260px + fixed cols so the Step column is never
  // crushed — the card scrolls horizontally instead.
  minWidth: 1400,
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
  '& th, & td': {
    padding: theme.spacing(1, 1.5),
    verticalAlign: 'top',
    // Solid colour instead of theme.palette.divider (rgba(0,0,0,0.12)) —
    // the translucent overlay blends differently against tinted row
    // backgrounds, so dividers appeared to change colour across a row.
    // A fixed light grey keeps them consistent everywhere.
    borderBottom: '1px solid #e0e0e0',
    fontSize: '0.85rem',
    // Long build / release names and step HTML wrap inside the fixed
    // cell widths instead of overflowing.
    wordBreak: 'break-word',
  },
  // Floor on the Step column (first). 260px minimum; can grow into free
  // space. Cell min-width is more reliable than <col> min-width alone.
  '& th:first-of-type, & td:first-of-type': {
    minWidth: 260,
  },
  '& th': {
    textAlign: 'left',
    fontWeight: 600,
    // Tightened from the default 0.85rem cell size so the region/preprod
    // headers fit on one line without wrapping or shrinking the column.
    fontSize: '0.7rem',
    backgroundColor: theme.palette.action.hover,
    borderBottom: '2px solid #e0e0e0',
  },
}));

// Column widths consumed by the <colgroup>. Edit + view modes share the
// same colgroup — per-step edit affordances live inside the Step cell so
// the table geometry never reshapes.
//
// Step floor is 260px (see Table cell rule + table minWidth). width here
// is the preferred share under table-layout:fixed so it isn't starved by
// the %-width Build column.
export const ColStep = styled('col')({
  width: '260px',
  minWidth: '260px',
});
// State / Owner slightly tighter than the original 200px so Step + Build
// reclaim width on laptop screens after the side chrome is collapsed.
export const ColState = styled('col')({ width: '160px' });
export const ColCurrent = styled('col')({ width: '72px' });
export const ColBuild = styled('col')({ width: '22%' });
export const ColRegion = styled('col')({ width: '72px' });
// Preprod columns carry an env label inside the header (`AUE-PREPROD` /
// `WEU-PREPROD`), so they need a touch more horizontal room than the
// plain region columns to fit "2604.0-prod"-style tag labels.
export const ColPreprodRegion = styled('col')({ width: '80px' });
export const ColOwner = styled('col')({ width: '160px' });

export const RegionCell = styled('td')({ textAlign: 'center' });
export const RegionHeader = styled('th')({ textAlign: 'center' });

export const StepHtml = styled('div')(({ theme }) => ({
  fontWeight: 500,
  lineHeight: 1.5,
  '& > *:first-child': { marginTop: 0 },
  '& > *:last-child': { marginBottom: 0 },
  '& p': { margin: theme.spacing(0.5, 0) },
  '& ul, & ol': {
    margin: theme.spacing(0.5, 0),
    paddingLeft: theme.spacing(3),
  },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
  '& code': {
    fontFamily: 'monospace',
    backgroundColor: theme.palette.action.hover,
    padding: '0 4px',
    borderRadius: 3,
  },
}));

// Step-cell footer line: catalog component link (when the row has one).
// Compact + slightly muted so it reads as auxiliary metadata.
export const CellFooterLinks = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  fontSize: '0.7rem',
  opacity: 0.8,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
}));

export const CellFooterLink = styled('span')({ fontSize: '0.7rem' });

export const EditTextButton = styled(Button)({
  fontSize: '0.7rem',
});

// Inline footer row inside the Step cell holding the per-step edit
// affordances (Edit step + move up/down + remove). Replaces the old
// separate Actions column so flipping a section into edit mode no longer
// reshapes the table geometry.
export const StepActionsRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(0.5),
  flexWrap: 'wrap',
}));

export const BuildStatusRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

export const BuildHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}));

export const BuildTime = styled('span')(({ theme }) => ({
  fontSize: '0.7rem',
  opacity: 0.7,
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

// Inline "Classic pipeline" label next to the build timestamp on Classic
// Release rows. Flags the rendering inconsistency between Classic Release
// and Build / YAML pipelines — purely informational, so the icon tint
// stays muted.
export const ClassicPipelineLabel = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.25),
  color: theme.palette.warning.main,
}));

export const ClassicPipelineWarning = styled('span')({
  fontSize: '0.9rem',
});

// Status-pill for build/release outcomes. `status` drives the palette.
// Neutral is used for stages that haven't reported back yet.
export type BuildStatus = 'succeeded' | 'failed' | 'inProgress' | 'neutral';
export const StatusChip = styled(Chip, {
  shouldForwardProp: prop => prop !== 'status',
})<{ status: BuildStatus }>(({ theme, status }) => {
  const base = {
    margin: 0,
    height: theme.spacing(2.5),
    fontSize: '0.7rem',
  } as const;
  switch (status) {
    case 'succeeded':
      // Match the `Tagged` state badge — both signal "good outcome".
      return {
        ...base,
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.contrastText,
      };
    case 'failed':
      return {
        ...base,
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
      };
    case 'inProgress':
      return {
        ...base,
        backgroundColor: theme.palette.info.main,
        color: theme.palette.info.contrastText,
      };
    case 'neutral':
    default:
      return {
        ...base,
        backgroundColor: theme.palette.action.disabledBackground,
      };
  }
});

// State dropdown — trigger inherits the row's state badge colour so it
// reads as the "live" state of the row. Compact vertically and stretched
// to fill the State cell width.
//
// Backstage's createUnifiedTheme prefixes MUI v5 classes with `v5-`, so
// the deep selectors target the prefixed forms.
export const StateSelectField = styled(TextField, {
  shouldForwardProp: prop => prop !== 'rowState',
})<{ rowState: RowState }>(({ theme, rowState }) => {
  const tone =
    rowState === 'not_ready'
      ? {
          backgroundColor: theme.palette.warning.light,
          color: theme.palette.warning.contrastText,
        }
      : rowState === 'tagged'
      ? {
          backgroundColor: theme.palette.success.light,
          color: theme.palette.success.contrastText,
        }
      : {
          backgroundColor: theme.palette.action.disabledBackground,
        };
  return {
    width: '100%',
    ...tone,
    '& [class*="v5-MuiInputBase-root"]': {
      fontSize: '0.8rem',
    },
    '& [class*="v5-MuiSelect-select"], & [class*="v5-MuiOutlinedInput-input"]':
      {
        padding: theme.spacing(0.5, 3, 0.5, 1),
      },
  };
});

// Whole-row tint. `rowState` is optional — undefined means no tint
// (default white background). `needsAttention` overrides the rowState
// tint with an error red — used to flag rows the user marked as
// `no_changes` while a same-major Bitbucket tag exists on the repo
// (i.e. the deployment likely should NOT have been skipped).
export const SectionRow = styled('tr', {
  shouldForwardProp: prop => prop !== 'rowState' && prop !== 'needsAttention',
})<{ rowState?: RowState; needsAttention?: boolean }>(
  ({ theme, rowState, needsAttention }) => {
    if (needsAttention) {
      return { backgroundColor: theme.palette.error.light };
    }
    if (rowState === 'not_ready') {
      return { backgroundColor: theme.palette.warning.light };
    }
    if (rowState === 'no_changes') {
      return { backgroundColor: theme.palette.action.disabledBackground };
    }
    return {};
  },
);

// Collapsed cell for Not ready / No changes rows — replaces Build + 6
// regions with a single italic muted message so the user knows the row
// is intentionally parked rather than missing data.
export const RowParkedMessage = styled('td')({
  fontStyle: 'italic',
  opacity: 0.6,
  fontSize: '0.8rem',
  textAlign: 'center',
  verticalAlign: 'middle',
});

export const RegionDash = styled('span')({ opacity: 0.4 });

// Green tint for ticked region checkboxes when they're disabled (view
// mode / Azure-driven / Classic Release stage-succeeded). DevOps asked
// for "this region is deployed" cells to stand out from a plain grey
// disabled tick. Pairs `Mui-checked` + `Mui-disabled` so editing keeps
// the default primary blue while the user is actively toggling.
export const DeployedCheckbox = styled(Checkbox)(({ theme }) => ({
  '&.Mui-checked.Mui-disabled': {
    color: theme.palette.success.main,
  },
}));

export const RegionTag = styled('span')(({ theme }) => ({
  fontSize: '0.65rem',
  opacity: 0.7,
  display: 'block',
  marginTop: theme.spacing(0.25),
}));

export const OwnerMissing = styled('span')({ opacity: 0.5 });

export const AddRow = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.background.default,
}));

// Mirrors the destructive button in ReleaseManagement so delete
// affordances read consistently across the release surface.
export const DangerOutlinedButton = styled(Button)(({ theme }) => ({
  color: theme.palette.error.main,
  borderColor: theme.palette.error.main,
  '&:hover': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    borderColor: theme.palette.error.main,
  },
}));

export const LockedBanner = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.warning.light,
  color: theme.palette.warning.contrastText,
  fontSize: '0.85rem',
}));
